'use client';

import React, { useMemo, useState } from 'react';
import Link from 'next/link';
import { ArticleCard, type ArticleCardArticle } from '@/components/ArticleCard';
import { SectionHead } from './SectionHead';
import type { GoalSlug } from '@/lib/aviado/goals';
import { cn } from '@/lib/utils';

/**
 * LatestResearch — "Latest research" w/ chip filter bar + 3-col grid.
 *
 * Spec: index.html `.latest` + `.latest__toolbar` + `.latest__grid`.
 *   - Chip bar: "All" + top goals with article counts
 *   - Active chip: warm-navy bg, white text
 *   - 3-col grid below with 9 most-recent articles matching the active filter
 *
 * Client-only filter state — no URL sync here; /browse page owns that.
 */

export interface LatestResearchProps {
  allCount: number;
  goalChips: Array<{ slug: GoalSlug; label: string; count: number }>;
  articlesByGoal: Partial<Record<GoalSlug | 'all', ArticleCardArticle[]>>;
}

export function LatestResearch({
  allCount,
  goalChips,
  articlesByGoal,
}: LatestResearchProps) {
  const [activeGoal, setActiveGoal] = useState<GoalSlug | 'all'>('all');

  const filtered = useMemo(() => {
    return articlesByGoal[activeGoal] ?? [];
  }, [articlesByGoal, activeGoal]);

  return (
    <section className="max-w-container mx-auto px-10 pt-24">
      <SectionHead
        title="Latest"
        accent="research"
        meta={<span>Updated weekly</span>}
        linkHref="/browse"
        linkLabel="Full archive →"
      />

      {/* Chip bar */}
      <div className="flex gap-2 overflow-x-auto mb-7 scrollbar-hide pb-3">
        <Chip
          isActive={activeGoal === 'all'}
          onClick={() => setActiveGoal('all')}
          label="All"
          count={allCount}
        />
        {goalChips.map((g) => (
          <Chip
            key={g.slug}
            isActive={activeGoal === g.slug}
            onClick={() => setActiveGoal(g.slug)}
            label={g.label}
            count={g.count}
          />
        ))}
      </div>

      {/* Grid */}
      {filtered.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-9 gap-x-7">
          {filtered.map((article) => (
            <ArticleCard key={article.id} article={article} variant="card" />
          ))}
        </div>
      ) : (
        <div className="py-12 text-center font-mono text-[12px] text-slate">
          No studies in this goal yet — browse{' '}
          <Link href="/browse" className="text-aviado-blue no-underline hover:underline">
            all research →
          </Link>
        </div>
      )}
    </section>
  );
}

function Chip({
  isActive,
  onClick,
  label,
  count,
}: {
  isActive: boolean;
  onClick: () => void;
  label: string;
  count: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isActive}
      className={cn(
        'shrink-0 inline-flex items-center gap-1.5',
        'min-h-12 px-4 py-2 rounded-pill border',
        'font-ui text-[12.5px] font-medium whitespace-nowrap cursor-pointer',
        'transition-colors duration-fast',
        isActive
          ? 'bg-warm-navy border-warm-navy text-white'
          : 'bg-transparent border-border text-slate hover:border-border-strong hover:text-ink'
      )}
    >
      {label}
      <span className="font-mono text-[10.5px] opacity-70 tabular-nums">{count}</span>
    </button>
  );
}

export default LatestResearch;
